// src/hooks/useMembershipsLive.js
// Whether channel and team memberships are live, for STATIC copy only
// (evergreen 3.5a; team keys 3.5b, 13.12).
//
// One read of settings/stripe (world-readable) per app session, shared by
// every caller:
//  - live: features.memberships.enabled === true. For availability claims
//    ("open on your channel", Join hints in copy).
//  - everLaunched: live, or features.memberships.firstLiveAt was ever set.
//    For compensation-source lists, so a paused launch never drops
//    memberships from them.
//  - teamLive: features.memberships.team.enabled === true. For team
//    availability claims (the squad chat confirm's closed line).
//  - teamEverLaunched: teamLive, or features.memberships.team.firstLiveAt
//    was ever set. For lists and help about existing team memberships.
//  - anyEverLaunched: everLaunched or teamEverLaunched. The gate of every
//    entry point to Your memberships (the Settings card, the Terms prompt's
//    manage link), so team memberships that go live while channel
//    memberships never launched still have a way in.
// The two kinds flip independently: the team keys never read the channel
// flag. Missing = false. A failed read is false for every key and is retried
// by the next mount. Never gates a purchase or a control: the server decides
// those.

import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const OFF = Object.freeze({
  live: false, everLaunched: false, teamLive: false, teamEverLaunched: false, anyEverLaunched: false,
});

let cached = null;   // { live, everLaunched, teamLive, teamEverLaunched, anyEverLaunched } once read
let inflight = null; // the one read in progress

export function membershipsLiveFrom(data) {
  const m = data?.features?.memberships || {};
  const live = m.enabled === true;
  const everLaunched = live || !!m.firstLiveAt;
  const t = m.team || {};
  const teamLive = t.enabled === true;
  const teamEverLaunched = teamLive || !!t.firstLiveAt;
  return {
    live,
    everLaunched,
    teamLive,
    teamEverLaunched,
    anyEverLaunched: everLaunched || teamEverLaunched,
  };
}

function readOnce() {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = getDoc(doc(db, 'settings', 'stripe'))
      .then((snap) => {
        cached = membershipsLiveFrom(snap.exists() ? snap.data() : null);
        return cached;
      })
      .catch((err) => {
        console.warn('[useMembershipsLive] settings read failed:', err?.code || err?.message);
        return OFF;
      })
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** { live, everLaunched, teamLive, teamEverLaunched, anyEverLaunched, loading } */
export default function useMembershipsLive() {
  const [state, setState] = useState(() => cached || OFF);
  const [loading, setLoading] = useState(() => !cached);

  useEffect(() => {
    if (cached) return undefined;
    let alive = true;
    readOnce().then((v) => {
      if (!alive) return;
      setState(v);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  return {
    live: state.live,
    everLaunched: state.everLaunched,
    teamLive: state.teamLive,
    teamEverLaunched: state.teamEverLaunched,
    anyEverLaunched: state.anyEverLaunched,
    loading,
  };
}

export { useMembershipsLive };
